// Pendoの制限 (ペイロード全体で512バイト) からマージンを取った値
const PENDO_LIMIT_BYTES = 480;

/** 1つの列が枠を使い切らないための上限。未指定の列は DEFAULT_FIELD_MAX_BYTES */
const FIELD_MAX_BYTES: Record<string, number> = {
  message: 160,
  reason: 120,
  src: 120,
  path: 80,
  from: 80,
  to: 80,
  pending: 120,
  after: 80,
  data: 80,
  // stack は frame 単位で切るので、残りの枠をすべて使ってよい
  stack: PENDO_LIMIT_BYTES,
};
const DEFAULT_FIELD_MAX_BYTES = 60;
/** これより短くしか入らない文字列は、意味を成さないので列ごと落とす */
const MIN_STRING_BYTES = 16;

type PendoField = readonly [key: string, value: unknown];
type PendoPayload = { event: string; [key: string]: unknown };

const truncateToBytes = (str: string, maxBytes: number): string => {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(str);
  if (encoded.length <= maxBytes) {
    return str;
  }

  const ELLIPSIS = '...';
  const targetBytes = maxBytes - ELLIPSIS.length;

  let result = '';
  let currentBytes = 0;

  for (const char of str) {
    const charBytes = encoder.encode(char).length;
    if (currentBytes + charBytes > targetBytes) {
      break;
    }
    result += char;
    currentBytes += charBytes;
  }

  return result + ELLIPSIS;
};

const getJsonByteSize = (obj: unknown): number => {
  return new TextEncoder().encode(JSON.stringify(obj)).length;
};

/** URL から、同一 origin なら origin を、chunk なら `.bundle.js` と query を削る */
const shortenUrl = (url: string, origin: string): string => {
  const withoutQuery = url.replace(/[?#].*$/, '');
  const sameOrigin = withoutQuery.startsWith(`${origin}/`);
  const shortened = sameOrigin
    ? withoutQuery.slice(origin.length + 1)
    : withoutQuery.replace(/^https?:\/\//, '');
  return shortened.replace(/\.bundle\.js$/, '');
};

/** stack の1行から、`<scheme>://...:<line>:<col>` の部分を取り出す */
const FRAME_PATTERN = /[a-z][a-z-]*:\/\/[^\s()]+?:\d+:\d+/;
/** frame の位置を、URL と `:<line>:<col>` に分ける */
const FRAME_POSITION_PATTERN = /^(.*)(:\d+:\d+)$/;

/**
 * stack を、file:line:col を空白区切りで並べた形に縮める。
 * message と同じ内容の1行目と、各 frame の origin・関数名を削り、限られた枠でより多くの frame を残す。
 */
const compressStack = (stack: string, origin: string): string => {
  const frames: string[] = [];
  for (const line of stack.split('\n')) {
    const match = FRAME_PATTERN.exec(line);
    if (match) {
      frames.push(
        match[0].replace(
          FRAME_POSITION_PATTERN,
          (_location, url: string, position: string) => `${shortenUrl(url, origin)}${position}`,
        ),
      );
    }
  }
  return frames.join(' ');
};

/** 空白区切りの stack を、frame の途中で切らずに `maxBytes` 以下に収める */
const truncateFrames = (frames: string, maxBytes: number): string => {
  let result = '';
  for (const frame of frames.split(' ')) {
    const next = result ? `${result} ${frame}` : frame;
    if (new TextEncoder().encode(next).length > maxBytes) {
      break;
    }
    result = next;
  }
  return result;
};

const serialize = (value: unknown): unknown => {
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return 'unserializable';
    }
  }
  return value;
};

/** `payload` に `key` を足したとき、`value` に使える残りのバイト数 */
const remainingBytesFor = (payload: PendoPayload, key: string): number => {
  return PENDO_LIMIT_BYTES - getJsonByteSize({ ...payload, [key]: '' });
};

/**
 * Pendo送信用に、ペイロード全体を {@link PENDO_LIMIT_BYTES} 以下に収める。
 *
 * `fields` は原因の特定に効く順に並べる。先頭から順に詰め、枠に収まらない列は
 * 文字列なら切り詰め（stack は frame 単位）、それ以外は落とす。
 * `undefined`・`null`・空文字の列は送らない。
 */
const sanitizeForPendo = (eventType: string, fields: readonly PendoField[]): PendoPayload => {
  const payload: PendoPayload = { event: eventType };

  for (const [key, rawValue] of fields) {
    const value = serialize(rawValue);
    if (value === undefined || value === null || value === '') {
      continue;
    }

    if (typeof value !== 'string') {
      if (getJsonByteSize({ ...payload, [key]: value }) <= PENDO_LIMIT_BYTES) {
        payload[key] = value;
      }
      continue;
    }

    // JSON の escape で増える分を見込み、収まるまで少しずつ削る
    let maxBytes = Math.min(FIELD_MAX_BYTES[key] ?? DEFAULT_FIELD_MAX_BYTES, remainingBytesFor(payload, key));
    while (maxBytes >= MIN_STRING_BYTES) {
      const fitted = key === 'stack' ? truncateFrames(value, maxBytes) : truncateToBytes(value, maxBytes);
      if (fitted && getJsonByteSize({ ...payload, [key]: fitted }) <= PENDO_LIMIT_BYTES) {
        payload[key] = fitted;
        break;
      }
      maxBytes -= 8;
    }
  }

  return payload;
};

export type { PendoField, PendoPayload };
export { compressStack, getJsonByteSize, PENDO_LIMIT_BYTES, sanitizeForPendo, shortenUrl, truncateToBytes };
